import { selectUserState } from 'src/lib/features/auth/authSlice';
import { useTypedSelector } from 'src/lib/store/store';
import { useKybVerificationQuery } from 'src/lib/endpoints/kyb/kyb.api';
import { cn } from '../ui-x';

type Nullable = string | null | undefined;

/* The default Cloudinary placeholder is not a real logo — fall back to initials. */
const isDefaultPhoto = (url: Nullable) =>
  !url || url.includes('fyd319ojzgxvzqlhgv9x');

export const initialsOf = (name: Nullable) => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'DW';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const CompanyAvatar = ({
  photo,
  name,
  className = 'h-7 w-7',
}: {
  photo?: Nullable;
  name?: Nullable;
  className?: string;
}) =>
  isDefaultPhoto(photo) ? (
    <span
      aria-hidden='true'
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-brand-navy text-[11px] font-bold text-white',
        className
      )}
    >
      {initialsOf(name)}
    </span>
  ) : (
    <img
      src={photo as string}
      alt=''
      aria-hidden='true'
      className={cn('shrink-0 rounded-full object-cover', className)}
    />
  );

/**
 * The header user pill (reference: client.tsx header, far right). Carries the
 * KYB verification state the old ClientAvatar showed as a ring badge.
 */
const UserInfo = () => {
  const user = useTypedSelector(selectUserState).user;
  const { kybRecords } = useKybVerificationQuery();

  const kybStatus = kybRecords?.data?.[0]?.status;
  const verified = kybStatus === 'verified' || kybStatus === 'completed';
  const name = user?.userCompany?.name ?? '';

  return (
    <span className='flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-1 pr-1 sm:pr-3'>
      <span className='relative shrink-0'>
        <CompanyAvatar photo={user?.photo} name={name} />
        <span
          title={verified ? 'Verified' : 'Unverified'}
          className={cn(
            'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background',
            verified ? 'bg-emerald-500' : 'bg-red-500'
          )}
        />
      </span>
      <span className='hidden min-w-0 sm:block'>
        <span className='block max-w-[9rem] truncate text-xs font-semibold leading-none text-brand-navy'>
          {name || 'My profile'}
        </span>
        <span className='mt-0.5 block text-[10px] uppercase tracking-wider text-muted-foreground'>
          Hiring Client
        </span>
      </span>
    </span>
  );
};

export default UserInfo;
